import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  Component,
  CpntList,
  useGetComponents,
  useUpdateComponentsDeletionFlags,
} from "../../services/components/";
import { useGetGeneralCodes } from "../../services/data-list";
import {
  InDefCommonFlagType,
  TcktStsTypeCpntPrjPrgrs,
} from "../../util/general-code/enums/general-codes";
import { ComponentAdditionPageMode } from "../component-addition/ComponentAdditionPageConstant";
import { useRootComponentEditDialogState } from "../dialog/root-component-edit-dialog";
import { RootComponentEditDialogContent } from "../dialog/root-component-edit-dialog/RootComponentEditDialogContent";

import {
  INIT_ERROR_RESULT_DATA,
  mappingErrorResponseToSpreadsheet,
} from "../../util";
import { PicAddressSpreadsheetState } from "../component-detail";
import {
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  isChecked,
  requestCdTypes,
} from "./ComponentListPageConstant";
import { ComponentListPageInitialState } from "./ComponentListPageInitialState";
import {
  ComponentInfoSpreadsheetRow,
  ComponentListPageState,
  ComponentListSpreadsheetState,
} from "./ComponentListPageState";
import { usePageState } from "./usePageState";
import { usePageNavigation } from "../use-page-navigation";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const ComponentListPageContent = () => {
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [rootComponentEditDialogState, setRootComponentEditDialogState] =
    useRootComponentEditDialogState();

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getComponentsResponse, sendGetComponentsRequest] = useGetComponents();
  const [
    getUpdateComponentsDeletionFlagsResponse,
    sendUpdateComponentsDeletionFlagsRequest,
  ] = useUpdateComponentsDeletionFlags();
  const { searchResult, dataList } = pageState as ComponentListPageState;
  const { messageForScreen } = useMessage();
  const { showConfirmationDialog, showToast, resetUserError } =
    useGlobalNotifier();
  const { initUserError } = usePageNavigation();
  const navigate = useNavigate();

  const [
    componentListSpreadsheetState,
    setComponentListSpreadsheetState,
    initComponentListSpreadsheetState,
  ] = useSpreadsheetState<ComponentListSpreadsheetState>();

  const componentListSpreadsheetInitialData =
    useMemo<ComponentListSpreadsheetState>(() => {
      const newComponentList: ComponentInfoSpreadsheetRow[] =
        searchResult.cpntList.map((e) => {
          const componentInfo: ComponentInfoSpreadsheetRow = {
            ...INIT_ERROR_RESULT_DATA,
            isEdited: false,
            component: e,
          };
          return componentInfo;
        });
      return {
        componentList: newComponentList,
        dataList,
      };
    }, [searchResult.cpntList, dataList]);

  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
    rootCpntId: queryParameter.get("rootCpntId") ?? "",
  };

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const onInitialDisplayed = () => {
    initUserError();
    setAppState((prevState) => ({ ...prevState, title: "Component List" }));
    sendGetGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendGetComponentsRequest({
      dipByRgn: params.dipByRgn,
      rootCpntId: params.rootCpntId,
    });
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];
    const cpntOwnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_OWN_TYPE,
      )?.cdList ?? [];

    const cpntAlctnType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CPNT_ALCTN_TYPE,
      )?.cdList ?? [];

    const clodSrvc =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC,
      )?.cdList ?? [];

    const clodSrvcRgn =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.CLOD_SRVC_RGN,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inDefCommonFlagType,
      },
      editDataList: {
        ...prevState.editDataList,
        cpntOwnType,
        cpntAlctnType,
        clodSrvc,
        clodSrvcRgn,
      },
    }));
  };

  const onGetComponentsResponse = () => {
    if (!getComponentsResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      searchResult: getComponentsResponse,
    }));
  };

  const onUpdateComponentsDeletionFlagsResponse = () => {
    if (!componentListSpreadsheetState) return;

    if (getUpdateComponentsDeletionFlagsResponse == undefined) return;

    if (Array.isArray(getUpdateComponentsDeletionFlagsResponse)) {
      const currentSpreadsheetRecordData: ComponentInfoSpreadsheetRow[] =
        componentListSpreadsheetState
          ? [...componentListSpreadsheetState.componentList]
          : [];
      const newSpreadsheetRecordData = mappingErrorResponseToSpreadsheet(
        getUpdateComponentsDeletionFlagsResponse,
        currentSpreadsheetRecordData,
        ComponentListPageInitialState.displayColumn,
      );

      setComponentListSpreadsheetState({
        ...componentListSpreadsheetState,
        componentList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetComponentsRequest({
        dipByRgn: params.dipByRgn,
        rootCpntId: params.rootCpntId,
      });
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetComponentsResponse, [getComponentsResponse]);
  useEffect(onUpdateComponentsDeletionFlagsResponse, [
    getUpdateComponentsDeletionFlagsResponse,
  ]);

  const editedRowExists = useMemo(
    () =>
      !!componentListSpreadsheetState?.componentList.some(
        (row) => row.isEdited === true,
      ),
    [componentListSpreadsheetState],
  );
  /**
   * Root Component
   */

  const [
    _picAddressSpreadsheetState,
    _setPicAddressSpreadsheetState,
    initPicAddressSpreadsheetState,
  ] = useSpreadsheetState<PicAddressSpreadsheetState>();

  const picAddressSpreadsheetInitialData = useMemo<PicAddressSpreadsheetState>(
    () => ({ results: searchResult.rootCpntIchrList }),
    [searchResult.rootCpntIchrList],
  );

  const onClickEditButton = () => {
    const showRootComponentDialog = () => {
      setRootComponentEditDialogState((prevState) => ({
        ...prevState,
        isShow: true,
        rootComponent: pageState.searchResult,
        dataList: pageState.editDataList,
      }));
    };

    if (editedRowExists) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          showRootComponentDialog();
        },
      });
    } else {
      showRootComponentDialog();
    }
  };

  const onCloseRootComponentEditDialog = () => {
    if (
      !rootComponentEditDialogState.isShow &&
      rootComponentEditDialogState.isUpdated
    ) {
      onInitialDisplayed();
    }
  };
  useEffect(onCloseRootComponentEditDialog, [
    rootComponentEditDialogState.isShow,
  ]);

  /**
   *  Component List
   */
  const isComponentKey = (arg: any): arg is keyof Component => {
    const componentKeys: (keyof Component)[] = ["cpntDelFlg"];
    return componentKeys.includes(arg);
  };
  const onCellEditedComponentListSpreadsheet = (event: CustomEvent) => {
    if (!componentListSpreadsheetState) return;
    if (event.detail.name === "on-cell-edited") return;
    const rowIndex: number = event.detail.cell.data.index;
    const value = event.detail.cell.value ?? "";
    const isChanged: boolean = event.detail.cell.row.isChanged;
    const eventName: string = event.detail.name;

    if (isComponentKey(eventName)) {
      const copyComponentList: ComponentListSpreadsheetState["componentList"] =
        JSON.parse(
          JSON.stringify(componentListSpreadsheetState?.componentList),
        );
      copyComponentList[rowIndex]["component"][eventName] = value;
      copyComponentList[rowIndex].isEdited = isChanged;

      const newState: ComponentListSpreadsheetState = {
        ...componentListSpreadsheetState,
        componentList: copyComponentList,
      };
      setComponentListSpreadsheetState(newState);
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!componentListSpreadsheetState) return;
    const newComponentList = _.cloneDeep(
      componentListSpreadsheetState?.componentList,
    ) as ComponentInfoSpreadsheetRow[];

    const isChanged: boolean = event.detail.cell.row.isChanged;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      newComponentList[rowIdx].component.cpntDelFlg = value;
      newComponentList[rowIdx].isEdited = isChanged;
    });

    setComponentListSpreadsheetState({
      ...componentListSpreadsheetState,
      componentList: newComponentList,
    });
  };

  const onClickComponentListSpreadsheet = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const component: Component = event.detail.value;

    if (eventName === "cpntId") {
      const navigationParameters = {
        rootCpntId: pageState.searchResult.rootCpntId,
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
        godFlg: InDefCommonFlagType.NO,
      };
      if (editedRowExists) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/component-detail?${stringify(navigationParameters)}`);
          },
        });
      } else {
        transition(`/component-detail?${stringify(navigationParameters)}`);
      }
    }
    if (eventName === "cpntId_newTabIcon") {
      const navigationParameters = {
        rootCpntId: pageState.searchResult.rootCpntId,
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
        godFlg: InDefCommonFlagType.NO,
      };
      const href = createPath({
        pathname: `/component-detail?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
    if (eventName === "partner") {
      const targetParams = {
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
      };
      if (editedRowExists) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/partner-list?${stringify(targetParams)}`);
          },
        });
      } else {
        transition(`/partner-list?${stringify(targetParams)}`);
      }
    }
    if (eventName === "partner_newTabIcon") {
      const targetParams = {
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
      };
      const href = createPath({
        pathname: `/partner-list?${stringify(targetParams)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
    if (eventName === "componentProject") {
      const targetParams = {
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
        cpntPjtSts: [
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN,
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
        ].join("_"),
      };
      if (editedRowExists) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/component-project-list?${stringify(targetParams)}`);
          },
        });
      } else {
        transition(`/component-project-list?${stringify(targetParams)}`);
      }
    }
    if (eventName === "componentProject_newTabIcon") {
      const targetParams = {
        cpntId: component.cpntId,
        dipByRgn: component.dipByRgn,
        cpntPjtSts: [
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_REQUIREMENT_DESIGN,
          TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
        ].join("_"),
      };
      const href = createPath({
        pathname: `/component-project-list?${stringify(targetParams)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    const onClickSaveButton = () => {
      showConfirmationDialog({
        title: "",
        type: "confirm",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          if (!componentListSpreadsheetState) return;
          const cpntList: CpntList[] =
            componentListSpreadsheetState?.componentList.flatMap((e, idx) => {
              if (e.isEdited) {
                return {
                  index: idx,
                  cpntId: e.component.cpntId,
                  delFlg: e.component.cpntDelFlg,
                  updateDateTime: e.component.updateDateTimeMCpnt,
                };
              }
              return [];
            });
          sendUpdateComponentsDeletionFlagsRequest({
            dipByRgn: params.dipByRgn,
            cpntList: cpntList,
          });
        },
      });
    };
    const onClickAddComponentButton = () => {
      const queryParams = {
        mode: ComponentAdditionPageMode.ADD_COMPONENT,
        rootCpntId: params.rootCpntId,
        dipByRgn: params.dipByRgn,
      };

      if (editedRowExists) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/component-addition?${stringify(queryParams)}`);
          },
        });
      } else {
        transition(`/component-addition?${stringify(queryParams)}`);
      }
    };

    return (
      <FrVrfFooter>
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          onClicked={onClickSaveButton}
          authActId="component_list.button.save"
          disabled={!editedRowExists}
        />
        <FrVrfButton
          content="Add Component"
          theme="primary"
          size="large"
          onClicked={onClickAddComponentButton}
          authActId="component_list.button.add_component"
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      {/* Root Component */}
      <FrVrfAccordion summary="Root component">
        <FrVrfPanel>
          <FrVrfFormPanel columns={2}>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Root Component ID"
                firstValue={searchResult.rootCpntId}
              />
              <FrVrfSingleLineDisplayField
                label="Root Component Name"
                firstValue={searchResult.rootCpntName}
              />
              <FrVrfSingleLineDisplayField
                label="DIP By Region"
                firstValue={searchResult.dipByRgn}
              />
              <FrVrfSingleLineDisplayField
                label="Owner Type"
                firstValue={searchResult.ownTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="System Name"
                firstValue={searchResult.systemName}
              />
              <FrVrfSingleLineDisplayField
                label="System ID"
                firstValue={searchResult.systemId}
              />
              <FrVrfSingleLineDisplayField
                label="Sub System ID"
                firstValue={searchResult.subSystemId}
              />
              <FrVrfCheckboxField
                labelContent="Not System Usage"
                checked={isChecked(searchResult.notSysUsg)}
                disabled
              />
            </FrVrfFormPanel>
            <FrVrfFormPanel>
              <FrVrfSingleLineDisplayField
                label="Component Allocation Type"
                firstValue={searchResult.alctnTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Services"
                firstValue={searchResult.clodSrvcTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Region"
                firstValue={searchResult.rgnTypeName}
              />
              <FrVrfSingleLineDisplayField
                label="Target Cloud Account"
                firstValue={searchResult.acntName}
              />
              <FrVrfPanel panelTitle="Pic Address">
                <FrVrfSpreadsheet
                  data={picAddressSpreadsheetInitialData}
                  templateUrl={TEMPLATE_FILE_URL.PIC_ADDRESS}
                  onInitialized={initPicAddressSpreadsheetState}
                  maxHeight={320}
                />
              </FrVrfPanel>
            </FrVrfFormPanel>
          </FrVrfFormPanel>
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Edit"
              size="medium"
              theme="secondary"
              onClicked={onClickEditButton}
              disabled={searchResult.rootCpntDelFlg === InDefCommonFlagType.YES}
              authActId="component_list.button.edit"
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <br />
      {/* Component */}
      <FrVrfSpreadsheet
        data={componentListSpreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_URL.COMPONENT_LIST}
        onInitialized={initComponentListSpreadsheetState}
        onCellClicked={onClickComponentListSpreadsheet}
        onCellEdited={onCellEditedComponentListSpreadsheet}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={740}
      />
      {/* Footer */}
      <FooterContent />
      {/* Root Component Edit Dialog */}
      {rootComponentEditDialogState.isShow && (
        <RootComponentEditDialogContent />
      )}
    </>
  );
};
